import {
	Avatar,
	Button,
	Flex,
	Icon,
	Text,
	WithDialog,
	WithTooltip,
	type ColorName,
	type IconName,
} from "@repo/ui";
import { tag } from "@repo/utils/typeUtils";
import { useCallback, useRef, useState } from "react";
import { useSetTaskAssignee } from "../hooks/useSetTaskAssignee";
import { agentPauseFor } from "../agentPause";
import { AgentPauseBanner } from "./AgentPauseBanner";
import { isErrorPayload } from "../activityLog/errorPayload";
import { ProviderNestedModal } from "../features/providers";
import { useLoggedInUser } from "../contexts/AuthContext";

import {
	ActivityType,
	type ActivityLogPayload,
	type ErrorPayload,
	type PhoneCallPayload,
	type PriorAuthResultPayload,
	type ReferralSentPayload,
} from "@repo/api/client";
import { formatTimestampRelative } from "@repo/utils/dateUtils";
import { activityMeta } from "../activityLog/activityTypeMeta";
import { formatSentDate } from "../referralSentState";
import { useApiClient } from "../contexts/ApiContext";
import { useEntity, useQuery, useTransaction } from "../db/DatabaseProvider";
import {
	ActivityLogItemId,
	TaskId,
	type ActivityLogItem,
	UserId,
} from "../db/schema";
import { parseMarkdown } from "../markdown/parser";
import { BulletListView, MarkdownDocView } from "./MarkdownDocView";
import { RunningJobBanner } from "./RunningJobBanner";
import { TextEditor, type TextEditorRef } from "./editor/TextEditor";
import { Experiment, useExperiment } from "../contexts/experiments";

/**
 * Formats a transcript array into a readable string with bolded roles
 * @param transcript - JSON string of transcript array or array of transcript messages
 * @returns Formatted transcript string
 */
function formatTranscript(transcript: string | undefined): string {
	if (!transcript) return "";

	try {
		const parsedTranscript =
			typeof transcript === "string" ? JSON.parse(transcript) : transcript;

		if (!Array.isArray(parsedTranscript)) return transcript;

		return parsedTranscript
			.map((message) => {
				if (message.role === "tool_call") {
					return `**Tool (${message.name}):** ${message.arguments}`;
				}
				const role = message.role === "user" ? "User" : "Agent";
				return `**${role}:** ${message.content}`;
			})
			.join("\n");
	} catch (_error) {
		// If parsing fails, return the original string
		return transcript;
	}
}

function isPriorAuthResultPayload(
	payload: ActivityLogPayload | undefined,
): payload is PriorAuthResultPayload {
	if (payload === null || payload === undefined) return false;
	// Check type discriminator first, then fall back to property check for legacy payloads
	if ("type" in payload && payload.type === ActivityType.PriorAuthResult)
		return true;
	return "success" in payload;
}

function isPhoneCallPayload(
	payload: ActivityLogPayload | undefined,
): payload is PhoneCallPayload {
	if (payload === null || payload === undefined) return false;
	// Check type discriminator first, then fall back to property check for legacy payloads
	if ("type" in payload && payload.type === ActivityType.PhoneCall) return true;
	return "transcript" in payload || "recordingUrl" in payload;
}

function getHostedRecordingUrl(
	payloadType: ActivityType | undefined,
	payload: ActivityLogPayload | undefined,
): string | undefined {
	if (!payload || !("recordingUrl" in payload)) return undefined;
	if (
		payloadType !== ActivityType.EligibilityCheckCompleted &&
		payloadType !== ActivityType.PriorAuthResult &&
		payloadType !== ActivityType.PriorAuthCompleted &&
		payloadType !== ActivityType.ReferralSent
	) {
		return undefined;
	}
	return typeof payload.recordingUrl === "string"
		? payload.recordingUrl
		: undefined;
}

function openExternalUrl(url: string) {
	window.open(url, "_blank", "noopener,noreferrer");
}

function LibrettoRecordingButton({ url }: { url: string }) {
	return (
		<Button variant="ghost" size="xs" onPress={() => openExternalUrl(url)}>
			View recording
		</Button>
	);
}

function ErrorDetailsActions({
	mediaItem,
	recordingUrl,
	onMediaClick,
}: {
	mediaItem?: { bucket: string; mediaUrl: string } | null;
	recordingUrl?: string;
	onMediaClick?: (bucketName: string, mediaKey: string) => void;
}) {
	if ((!mediaItem || !onMediaClick) && !recordingUrl) return null;

	return (
		<Flex gap="xs" wrap>
			{mediaItem && onMediaClick && (
				<Button
					variant="ghost"
					size="sm"
					color="accent11"
					onPress={() => onMediaClick(mediaItem.bucket, mediaItem.mediaUrl)}
				>
					View screenshot
				</Button>
			)}
			{recordingUrl && (
				<Button
					variant="ghost"
					size="sm"
					color="accent11"
					onPress={() => openExternalUrl(recordingUrl)}
				>
					View recording
				</Button>
			)}
		</Flex>
	);
}

type ActivityDisplayResult = {
	title: string;
	description: string | undefined;
	icon?: IconName;
	color?: ColorName;
};

/**
 * Formats an activity log payload into a title, description, icon, and color for display.
 * Title should be immediately scannable; description contains supplementary details.
 * Icon and color override the defaults from activityMeta when the payload state requires it.
 */
function formatActivityDisplay(
	payload: ActivityLogPayload,
): ActivityDisplayResult {
	const normalizeText = (value: string) => value.replace(/\s+/g, " ").trim();
	// The 80 and the boundary regex below are copied into `summarise` in
	// apps/api/src/tools/executeBrowserAgentJob.test.ts, which asserts the
	// precondition copy still says something after this runs. Change either and
	// that test is measuring the wrong thing -- it cannot import from here.
	const truncateToSentence = (value: string, max = 80) => {
		// Find first sentence boundary
		const sentenceEnd = value.search(/[.!?](\s|$)/);
		const firstSentence =
			sentenceEnd > 0 ? value.slice(0, sentenceEnd + 1) : value;
		// Then apply character limit
		return firstSentence.length > max
			? `${firstSentence.slice(0, max).trim()}...`
			: firstSentence;
	};

	switch (payload.type) {
		case ActivityType.Error:
			return {
				title: "System error",
				description: undefined,
			};
		case ActivityType.PriorAuthResult: {
			// Failures only: a successful row carries a `displayMessage` too, and it
			// restates the title ("Prior authorization submitted." under "Referral
			// auth approved"). The old code was failure-only by accident, since it
			// keyed on `errorCode`, which success rows never set.
			//
			// `displayMessage` is the sentence written for staff; `errorCode` is a
			// bare token like USER_ACTION_REQUIRED. Preferring the code put that
			// token on screen, so read it only as a fallback for older rows that
			// carry no message.
			const failureText = payload.success
				? undefined
				: (payload.displayMessage ?? payload.errorCode);
			return {
				title: payload.success
					? "Referral auth approved"
					: "Referral auth failed",
				description: failureText
					? truncateToSentence(normalizeText(failureText))
					: undefined,
				icon: payload.success ? "CircleCheckBig" : "CircleX",
				color: payload.success ? "green11" : "red11",
			};
		}
		case ActivityType.PriorAuthCompleted:
			return {
				title: `Completed prior auth #${payload.authorizationCode}`,
				description: payload.numberOfVisits
					? `${payload.numberOfVisits} visits`
					: undefined,
			};
		case ActivityType.EligibilityCheckCompleted:
			return {
				title: payload.isEligible
					? "Verified eligibility"
					: "Eligibility check failed",
				description: payload.planName ?? undefined,
				icon: payload.isEligible ? "ShieldCheck" : "ShieldX",
				color: payload.isEligible ? "green11" : "red11",
			};
		case ActivityType.ReferralSent:
			if (payload.sentOutsideSkyway) {
				const sentOn = formatSentDate(payload.sentOutsideSkyway.sentDate);
				return {
					title: "Marked as sent outside Skyway",
					description: payload.sentOutsideSkyway.patientNotificationSkipped
						? `Sent ${sentOn}. Patient not notified`
						: `Sent ${sentOn}`,
				};
			}
			return {
				title: payload.providerName
					? `Sent referral to ${payload.providerName}`
					: "Sent referral",
				description: undefined,
			};
		case ActivityType.ConsentUpdate: {
			// Use status field if present, otherwise fall back to consented boolean
			const status =
				payload.status ?? (payload.consented ? "granted" : "requested");
			const titleMap = {
				requested: "Consent requested",
				granted: "Consent granted",
				declined: "Consent declined",
			};
			const iconMap = {
				requested: "ShieldEllipsis" as const,
				granted: "ShieldCheck" as const,
				declined: "ShieldX" as const,
			};
			const colorMap = {
				requested: "amber11" as const,
				granted: "green11" as const,
				declined: "red11" as const,
			};
			return {
				title: titleMap[status],
				description: undefined,
				icon: iconMap[status],
				color: colorMap[status],
			};
		}
		case ActivityType.SpruceMessageSent:
			return {
				title: "Sent message in Spruce",
				description: payload.messageText
					? truncateToSentence(normalizeText(payload.messageText))
					: undefined,
			};
		case ActivityType.SpruceMessageReceived:
			return {
				title: "Received message in Spruce",
				description: payload.messageText
					? truncateToSentence(normalizeText(payload.messageText))
					: undefined,
			};
		case ActivityType.SpruceInternalNote:
			return {
				title: "Added internal note",
				description: payload.noteText
					? truncateToSentence(normalizeText(payload.noteText))
					: undefined,
			};
		case ActivityType.SearchProviders: {
			if (payload.success === false) {
				return {
					title: "No providers found",
					description: payload.errorMessage
						? truncateToSentence(normalizeText(payload.errorMessage))
						: undefined,
					icon: "CircleX",
					color: "red11",
				};
			}

			// Build filter summary similar to backend searchProviders
			const filters: string[] = [];
			if (payload.specialty) filters.push(payload.specialty);
			if (payload.providerName) filters.push(`"${payload.providerName}"`);
			if (payload.organizationName)
				filters.push(`at "${payload.organizationName}"`);
			if (payload.locations?.length)
				filters.push(`in ${payload.locations.join(", ")}`);
			if (payload.acceptsTelehealth) filters.push("telehealth");

			const filterStr = filters.length > 0 ? filters.join(" ") : undefined;
			const count = payload.resultsCount ?? 0;

			return {
				title: `Found ${count} provider${count === 1 ? "" : "s"}`,
				description: filterStr
					? truncateToSentence(normalizeText(filterStr))
					: undefined,
			};
		}
		case ActivityType.Unsnooze:
			return {
				title:
					payload.reason === "manual"
						? "Manually unsnoozed"
						: payload.reason === "auto_retry"
							? "Retried automatically"
							: "Snooze expired",
				description: undefined,
			};
		case ActivityType.ReassignToUser:
			return {
				title: payload.assignedToUserName
					? `Assigned to ${payload.assignedToUserName}`
					: "Reassigned to user",
				description: undefined,
			};
		case ActivityType.ReassignToAgent:
			return {
				title: payload.reassignedByUserName
					? `Assigned to agent by ${payload.reassignedByUserName}`
					: "Assigned to agent",
				description: undefined,
			};
		case ActivityType.Snooze: {
			if (payload.unsnoozeDate) {
				const unsnoozeDate = new Date(payload.unsnoozeDate);
				const now = new Date();
				const diffMs = unsnoozeDate.getTime() - now.getTime();
				const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

				if (diffDays > 0) {
					return {
						title: `Snoozed for ${diffDays}d`,
						description: undefined,
					};
				}
			}
			return { title: "Snoozed", description: undefined };
		}
		case ActivityType.TaskCompleted:
			return { title: "Completed task", description: undefined };
		case ActivityType.Comment:
			return {
				title: "Commented",
				description: payload.content
					? truncateToSentence(normalizeText(payload.content))
					: undefined,
			};
		case ActivityType.EHRComment:
			return {
				title: "EHR comment",
				description: payload.content
					? truncateToSentence(normalizeText(payload.content))
					: undefined,
			};
		case ActivityType.PhoneCall: {
			// Build concise description from available payload data
			const parts: string[] = [];
			if (payload.callReceiver) parts.push(payload.callReceiver);
			if (payload.query) parts.push(payload.query);

			return {
				title: payload.callReceiver
					? `Called ${payload.callReceiver}`
					: "Made phone call",
				description:
					parts.length > 1
						? truncateToSentence(normalizeText(payload.query ?? ""))
						: payload.result
							? truncateToSentence(normalizeText(payload.result))
							: undefined,
			};
		}
	}
}

type ActivityLogItemProps = {
	itemId: ActivityLogItemId;
	// We don't show unsnooze logs unless they're the last
	isLast?: boolean;
};

function TranscriptDialogButton({ transcript }: { transcript: string }) {
	return (
		<WithDialog
			title="Call Transcript"
			dialog={
				<Flex column gap="md" overflow="y" style={{ maxHeight: "80vh" }}>
					<MarkdownDocView
						doc={parseMarkdown(formatTranscript(transcript))}
						size="md"
					/>
				</Flex>
			}
		>
			<Button variant="ghost" size="xs" icon="FileText">
				View Transcript
			</Button>
		</WithDialog>
	);
}

function PlayRecordingButton({
	bucketName,
	mediaKey,
	onPress,
}: {
	bucketName: string;
	mediaKey: string;
	onPress: (bucketName: string, mediaKey: string) => void;
}) {
	return (
		<Button
			variant="ghost"
			size="xs"
			onPress={() => onPress(bucketName, mediaKey)}
			icon="AudioLines"
		>
			Play Recording
		</Button>
	);
}

function AttachedDocumentsDialogButton({ documents }: { documents: string[] }) {
	return (
		<WithDialog
			title="Attached Documents"
			dialog={
				<Flex column gap="lg" overflow="y" style={{ maxHeight: "80vh" }}>
					{documents.length > 0 ? (
						<BulletListView
							node={{
								type: "bulletList",
								items: documents.map((doc: string) => ({
									type: "listItem",
									content: [{ type: "text", text: doc }],
								})),
							}}
							size="md"
						/>
					) : (
						<Text size="md" color="sand11">
							No documents were attached to this referral.
						</Text>
					)}
				</Flex>
			}
		>
			<Button variant="ghost" size="xs">
				View Documents ({documents.length})
			</Button>
		</WithDialog>
	);
}

type InlineActivityLogProps = {
	icon: React.ComponentProps<typeof Icon>["name"];
	text: string;
	color: React.ComponentProps<typeof Icon>["color"];
	timestamp: Date;
	summary?: string;
	children?: React.ReactNode;
};

export function InlineActivityLog({
	icon,
	text,
	color,
	timestamp,
	summary,
	children,
}: InlineActivityLogProps) {
	return (
		<Flex align="center" gap="md" paddingHorizontal="md">
			<Icon name={icon} color={color} size="md" shrink={0} />
			<Text size="sm" weight="medium" color={color} shrink={0}>
				{text}
			</Text>
			{summary && (
				<>
					<Text size="sm" color="sand11" shrink={0}>
						—
					</Text>
					<Text size="sm" color="sand11" shrink grow truncate={1}>
						{summary}
					</Text>
				</>
			)}
			<Flex style={{ flex: 1 }} />
			<Flex shrink={0} gap="md" align="center">
				{children}
				<WithTooltip
					content={new Date(timestamp).toLocaleDateString("en-US", {
						weekday: "short",
						year: "numeric",
						month: "short",
						day: "numeric",
					})}
				>
					<Text size="sm" color="sand11" focusable>
						{formatTimestampRelative(timestamp)}
					</Text>
				</WithTooltip>
			</Flex>
		</Flex>
	);
}

type ExpandedActivityLogProps = {
	leading: React.ReactNode;
	title: string;
	titleColor?: ColorName;
	timestamp: Date;
	children: React.ReactNode;
	headerActions?: React.ReactNode;
};

function ExpandedActivityLog({
	leading,
	title,
	titleColor,
	timestamp,
	children,
	headerActions,
}: ExpandedActivityLogProps) {
	return (
		<Flex
			gap="md"
			align="start"
			padding="md"
			background="background"
			borderRadius="md"
			shadow="subtle"
			style={{ flex: 1 }}
		>
			{leading}
			<Flex column gap="xs" style={{ flex: 1 }}>
				<Flex
					align="center"
					gap="md"
					wrap
					style={{ flex: 1 }}
					justify="space-between"
				>
					<Text size="sm" weight="medium" color={titleColor}>
						{title}
					</Text>
					<Flex align="center" gap="xs" shrink={0}>
						{headerActions}
						<WithTooltip
							content={new Date(timestamp).toLocaleDateString("en-US", {
								weekday: "short",
								year: "numeric",
								month: "short",
								day: "numeric",
							})}
						>
							<Text size="sm" color="sand11" focusable>
								{formatTimestampRelative(timestamp)}
							</Text>
						</WithTooltip>
					</Flex>
				</Flex>
				{children}
			</Flex>
		</Flex>
	);
}

type CommentActivityLogProps = {
	content: string;
	creatorName: string;
	creatorImageUrl?: string;
	timestamp: Date;
};

export function CommentActivityLog({
	content,
	creatorName,
	creatorImageUrl,
	timestamp,
}: CommentActivityLogProps) {
	return (
		<ExpandedActivityLog
			leading={<Avatar name={creatorName} image={creatorImageUrl} />}
			title={creatorName}
			timestamp={timestamp}
		>
			<MarkdownDocView doc={parseMarkdown(content)} size="prose-sm" />
		</ExpandedActivityLog>
	);
}

type SpruceMessageActivityLogProps = {
	messageText: string;
	direction: "sent" | "received";
	timestamp: Date;
};

export function SpruceMessageActivityLog({
	messageText,
	direction,
	timestamp,
}: SpruceMessageActivityLogProps) {
	const isSent = direction === "sent";
	return (
		<ExpandedActivityLog
			leading={
				<Icon
					name={isSent ? "Reply" : "Inbox"}
					color={isSent ? "blue11" : "accent11"}
					size="md"
					shrink={0}
				/>
			}
			title={isSent ? "Sent message in Spruce" : "Received message in Spruce"}
			titleColor={isSent ? "blue11" : "accent11"}
			timestamp={timestamp}
		>
			<MarkdownDocView doc={parseMarkdown(messageText)} size="prose-sm" />
		</ExpandedActivityLog>
	);
}

type ErrorActivityLogProps = {
	payload: ErrorPayload;
	timestamp: Date;
	content?: string;
	isUserActionable?: boolean;
	mediaItem?: { bucket: string; mediaUrl: string };
	onMediaClick?: (bucketName: string, mediaKey: string) => void;
};

export function ErrorActivityLog({
	payload,
	timestamp,
	content,
	isUserActionable,
	mediaItem,
	onMediaClick,
}: ErrorActivityLogProps) {
	const userActionable = isUserActionable ?? payload.isUserActionable;
	const color = userActionable ? "amber11" : "red11";
	// Nobody needs to act while an automatic rerun is pending, so it reads as
	// neither a task for staff nor one for support.
	const title = payload.autoRetry
		? "Retrying automatically"
		: userActionable
			? "Action Required"
			: "Error";

	return (
		<ExpandedActivityLog
			leading={<Icon name="AlertCircle" color={color} size="md" shrink={0} />}
			title={title}
			titleColor={color}
			timestamp={timestamp}
			headerActions={
				<>
					<WithDialog
						title="Error Details"
						dialog={
							<Flex column gap="lg" overflow="y" style={{ maxHeight: "80vh" }}>
								{payload.toolName && (
									<Flex column gap="xs">
										<Text size="md" weight="semibold">
											Tool Name
										</Text>
										<Text
											size="md"
											style={{
												fontFamily: "monospace",
											}}
										>
											{payload.toolName}
										</Text>
									</Flex>
								)}
								{payload.toolInput && (
									<Flex column gap="xs">
										<Text size="md" weight="semibold">
											Tool Input
										</Text>
										<Text
											size="md"
											style={{
												fontFamily: "monospace",
												whiteSpace: "pre-wrap",
												wordBreak: "break-word",
											}}
										>
											{JSON.stringify(payload.toolInput, null, 2)}
										</Text>
									</Flex>
								)}
								{payload.internalError && (
									<Flex column gap="xs">
										<Text size="md" weight="semibold">
											Error Message
										</Text>
										<Text
											size="md"
											style={{
												fontFamily: "monospace",
												whiteSpace: "pre-wrap",
												wordBreak: "break-word",
											}}
										>
											{payload.internalError}
										</Text>
									</Flex>
								)}
								<ErrorDetailsActions
									mediaItem={mediaItem}
									recordingUrl={payload.recordingUrl}
									onMediaClick={onMediaClick}
								/>
							</Flex>
						}
					>
						<Button variant="ghost" size="xs" color={color}>
							See Details
						</Button>
					</WithDialog>
					{mediaItem && onMediaClick && (
						<Button
							variant="ghost"
							size="xs"
							color="blue11"
							onPress={() => onMediaClick(mediaItem.bucket, mediaItem.mediaUrl)}
						>
							View Screenshot
						</Button>
					)}
				</>
			}
		>
			<Flex column gap="xs">
				{content && (
					<MarkdownDocView doc={parseMarkdown(content)} size="prose-sm" />
				)}
				{!userActionable && (
					<Text size="sm" color="red11" weight="semibold">
						Please contact Skyway support for assistance with this issue.
					</Text>
				)}
			</Flex>
		</ExpandedActivityLog>
	);
}

function ActivityLogItemV2({ itemId, isLast }: ActivityLogItemProps) {
	const item = useEntity("activityLogItem", itemId);
	const creator = useEntity("user", item?.createdBy);
	const apiClient = useApiClient();

	if (!item) {
		return null;
	}

	const payloadType = item.payload?.type;
	if (!isLast && payloadType === ActivityType.Unsnooze) return null;
	const hostedRecordingUrl = getHostedRecordingUrl(payloadType, item.payload);

	const handleMediaClick = async (bucketName: string, mediaKey: string) => {
		try {
			const { signedUrls } = await apiClient.tools.generateMediaSignedUrls({
				bucketName,
				mediaKeys: [mediaKey],
			});
			if (signedUrls.length > 0) {
				window.open(signedUrls[0]?.signedUrl, "_blank");
			}
		} catch (error) {
			console.error("Error generating signed URL:", error);
		}
	};

	const meta = payloadType ? activityMeta[payloadType] : undefined;

	// Handle Error activity type
	if (payloadType === ActivityType.Error && isErrorPayload(item.payload)) {
		// Fall back to item.content for legacy data
		const errorContent = item.content;
		return (
			<ErrorActivityLog
				payload={item.payload}
				content={errorContent}
				timestamp={item.createdAt}
				isUserActionable={item.payload.isUserActionable === true}
				mediaItem={item.mediaItem ?? undefined}
				onMediaClick={handleMediaClick}
			/>
		);
	}

	// Handle Comment and EHRComment activity types
	if (
		payloadType === ActivityType.Comment ||
		payloadType === ActivityType.EHRComment
	) {
		const commentContent =
			("content" in item.payload ? item.payload.content : undefined) ??
			item.content ??
			"";
		return (
			<CommentActivityLog
				content={commentContent}
				creatorName={creator?.name || "Unknown User"}
				creatorImageUrl={creator?.imageUrl || undefined}
				timestamp={item.createdAt}
			/>
		);
	}

	// Handle Spruce messages - show expanded like comments
	if (
		payloadType === ActivityType.SpruceMessageSent ||
		payloadType === ActivityType.SpruceMessageReceived
	) {
		const payload = item.payload;
		// Fall back to item.content for legacy data
		const messageText =
			("messageText" in payload ? payload.messageText : undefined) ??
			item.content ??
			"";
		return (
			<SpruceMessageActivityLog
				messageText={messageText}
				direction={
					payloadType === ActivityType.SpruceMessageSent ? "sent" : "received"
				}
				timestamp={item.createdAt}
			/>
		);
	}

	// Handle all other activity types with InlineActivityLog
	if (meta) {
		const display = item.payload
			? formatActivityDisplay(item.payload)
			: undefined;

		// Use icon/color from display if provided (payload-specific), otherwise fall back to meta defaults
		const iconName = display?.icon ?? meta.icon;
		const iconColor = display?.color ?? meta.color;

		return (
			<InlineActivityLog
				icon={iconName}
				text={display?.title ?? meta.label}
				color={iconColor}
				timestamp={item.createdAt}
				summary={display?.description}
			>
				{item.mediaItem && (
					<Button
						variant="ghost"
						size="xs"
						onPress={() =>
							handleMediaClick(item.mediaItem!.bucket, item.mediaItem!.mediaUrl)
						}
					>
						View Screenshot
					</Button>
				)}
				{payloadType === ActivityType.PhoneCall &&
					isPhoneCallPayload(item.payload) && (
						<>
							{item.payload.transcript && (
								<TranscriptDialogButton transcript={item.payload.transcript} />
							)}
							{item.payload.recordingUrl && item.payload.recordingBucket && (
								<PlayRecordingButton
									bucketName={item.payload.recordingBucket}
									mediaKey={item.payload.recordingUrl}
									onPress={handleMediaClick}
								/>
							)}
						</>
					)}
				{/* An outside-Skyway send has no attached documents to show. */}
				{payloadType === ActivityType.ReferralSent &&
					!(item.payload as ReferralSentPayload).sentOutsideSkyway && (
						<AttachedDocumentsDialogButton
							documents={
								(item.payload as ReferralSentPayload).attachedDocuments ?? []
							}
						/>
					)}
				{hostedRecordingUrl && (
					<LibrettoRecordingButton url={hostedRecordingUrl} />
				)}
			</InlineActivityLog>
		);
	}

	// Fallback for items without meta (shouldn't happen in practice)
	// Fall back to item.content for legacy data
	return (
		<CommentActivityLog
			content={item.content ?? ""}
			creatorName={creator?.name || "Unknown User"}
			creatorImageUrl={creator?.imageUrl || undefined}
			timestamp={item.createdAt}
		/>
	);
}

function ActivityLogItem({ itemId, isLast }: ActivityLogItemProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const item = useEntity("activityLogItem", itemId);
	const creator = useEntity("user", item?.createdBy);
	const apiClient = useApiClient();
	const hasReasoning =
		item?.payload && "reasoning" in item.payload && item.payload.reasoning;

	if (!item) {
		return null;
	}

	const payloadType = item.payload?.type;
	if (!isLast && payloadType === ActivityType.Unsnooze) return null;
	const hostedRecordingUrl = getHostedRecordingUrl(payloadType, item.payload);

	const handleMediaClick = async (bucketName: string, mediaKey: string) => {
		try {
			const { signedUrls } = await apiClient.tools.generateMediaSignedUrls({
				bucketName,
				mediaKeys: [mediaKey],
			});
			if (signedUrls.length > 0) {
				window.open(signedUrls[0]?.signedUrl, "_blank");
			}
		} catch (error) {
			console.error("Error generating signed URL:", error);
		}
	};

	const meta = payloadType ? activityMeta[payloadType] : undefined;

	// Handle conditional rendering for PriorAuthResult based on success
	let iconName = meta?.icon;
	let iconColor = meta?.color;
	let label = meta?.label;

	// An error the clinic can resolve stays theirs to resolve wherever it sits in
	// the log. Gating this on `isLast` meant the agent's next activity item, which
	// `processUserActionRequired` enqueues immediately, relabelled it "Error"
	// seconds later -- and in the refreshed renderer also appended "contact
	// Skyway support" to something support cannot fix.
	if (
		payloadType === ActivityType.Error &&
		isErrorPayload(item.payload) &&
		item.payload.isUserActionable === true
	) {
		label = item.payload.autoRetry
			? "Retrying automatically"
			: "Action Required";
	}

	if (
		item.payload?.type === ActivityType.ReferralSent &&
		item.payload.sentOutsideSkyway
	) {
		label = "Marked as sent outside Skyway";
	}

	if (
		payloadType === ActivityType.PriorAuthResult &&
		isPriorAuthResultPayload(item.payload)
	) {
		const payload = item.payload;
		iconName = payload.success ? "CircleCheckBig" : "CircleX";
	}

	return (
		<Flex gap="sm" align="start" style={{ flex: 1 }}>
			{/* User Avatar */}
			{iconName ? (
				<Icon name={iconName} color={iconColor} size="md" shrink={0} />
			) : (
				<Avatar
					size="md"
					name={creator?.name || "Unknown User"}
					image={creator?.imageUrl || undefined}
				/>
			)}
			{/* Activity Content */}
			<Flex column gap="xs" style={{ flex: 1 }}>
				<Flex align="center" gap="xs" style={{ flex: 1 }}>
					<Flex align="center" gap="xs" style={{ flex: 1 }}>
						{label ? (
							<Text color={meta?.color} weight="medium" size="sm">
								{label}
							</Text>
						) : (
							<Text size="sm" weight="medium">
								{creator?.name || "Unknown User"}
							</Text>
						)}
						<WithTooltip
							content={new Date(item.createdAt).toLocaleDateString("en-US", {
								weekday: "short",
								year: "numeric",
								month: "short",
								day: "numeric",
							})}
						>
							<Text size="sm" color="sand11" focusable>
								{formatTimestampRelative(item.createdAt)}
							</Text>
						</WithTooltip>
					</Flex>
					{hasReasoning && (
						<Flex
							align="center"
							style={{ cursor: "pointer" }}
							onClick={() => setIsExpanded(!isExpanded)}
						>
							<Icon
								name={isExpanded ? "ChevronsDownUp" : "ChevronsUpDown"}
								size="sm"
								color="sand11"
							/>
						</Flex>
					)}
					{item.mediaItem && (
						<Button
							variant="ghost"
							size="xs"
							onPress={() =>
								handleMediaClick(
									item.mediaItem!.bucket,
									item.mediaItem!.mediaUrl,
								)
							}
						>
							View Screenshot
						</Button>
					)}
					{payloadType === ActivityType.PhoneCall &&
						isPhoneCallPayload(item.payload) &&
						(() => {
							const payload = item.payload as PhoneCallPayload;
							return (
								<Flex gap="xs">
									{payload.transcript && (
										<WithDialog
											title="Call Transcript"
											dialog={
												<Flex
													column
													gap="md"
													overflow="y"
													style={{ maxHeight: "80vh" }}
												>
													<MarkdownDocView
														doc={parseMarkdown(
															formatTranscript(payload.transcript),
														)}
														size="sm"
													/>
												</Flex>
											}
										>
											<Button variant="ghost" size="xs" color="green11">
												View Transcript
											</Button>
										</WithDialog>
									)}
									{payload.recordingUrl && payload.recordingBucket && (
										<Button
											variant="ghost"
											size="xs"
											color="blue11"
											onPress={() =>
												handleMediaClick(
													payload.recordingBucket!,
													payload.recordingUrl!,
												)
											}
										>
											Play Recording
										</Button>
									)}
								</Flex>
							);
						})()}
					{payloadType === ActivityType.Error &&
						isErrorPayload(item.payload) &&
						(() => {
							const payload = item.payload as ErrorPayload;
							return (
								<WithDialog
									title="Error Details"
									dialog={
										<Flex
											column
											gap="md"
											overflow="y"
											style={{ maxHeight: "80vh" }}
										>
											{payload.toolName && (
												<Flex column gap="xs">
													<Text size="md" weight="semibold">
														Tool Name
													</Text>
													<Text
														size="sm"
														style={{
															fontFamily: "monospace",
														}}
													>
														{payload.toolName}
													</Text>
												</Flex>
											)}
											{payload.toolInput && (
												<Flex column gap="xs">
													<Text size="md" weight="semibold">
														Tool Input
													</Text>
													<Text
														size="sm"
														style={{
															fontFamily: "monospace",
															whiteSpace: "pre-wrap",
															wordBreak: "break-word",
														}}
													>
														{JSON.stringify(payload.toolInput, null, 2)}
													</Text>
												</Flex>
											)}
											{payload.internalError && (
												<Flex column gap="xs">
													<Text size="md" weight="semibold">
														Error Message
													</Text>
													<Text
														size="sm"
														style={{
															fontFamily: "monospace",
															whiteSpace: "pre-wrap",
															wordBreak: "break-word",
														}}
													>
														{payload.internalError}
													</Text>
												</Flex>
											)}
											<ErrorDetailsActions
												mediaItem={item.mediaItem}
												recordingUrl={payload.recordingUrl}
												onMediaClick={handleMediaClick}
											/>
										</Flex>
									}
								>
									<Button variant="ghost" size="xs" color="red11">
										See Details
									</Button>
								</WithDialog>
							);
						})()}
					{payloadType === ActivityType.ReferralSent &&
						!(item.payload as ReferralSentPayload).sentOutsideSkyway &&
						(() => {
							const payload = item.payload as ReferralSentPayload;
							const attachedDocuments = payload.attachedDocuments ?? [];
							return (
								<WithDialog
									title="Attached Documents"
									dialog={
										<Flex
											column
											gap="md"
											overflow="y"
											style={{ maxHeight: "80vh" }}
										>
											{attachedDocuments.length > 0 ? (
												<BulletListView
													node={{
														type: "bulletList",
														items: attachedDocuments.map((doc: string) => ({
															type: "listItem",
															content: [{ type: "text", text: doc }],
														})),
													}}
													size="md"
												/>
											) : (
												<Text size="md" color="sand11">
													No documents were attached to this referral.
												</Text>
											)}
										</Flex>
									}
								>
									<Button variant="ghost" size="xs" color="blue11">
										View Documents ({attachedDocuments.length})
									</Button>
								</WithDialog>
							);
						})()}
					{hostedRecordingUrl && (
						<LibrettoRecordingButton url={hostedRecordingUrl} />
					)}
				</Flex>
				<Flex column gap="xs">
					{/* Fall back to item.content for legacy data */}
					<MarkdownDocView
						doc={parseMarkdown(item.content ?? "")}
						size="prose-sm"
					/>
					{/* Show "contact support" message for system errors */}
					{payloadType === ActivityType.Error &&
						isErrorPayload(item.payload) &&
						!item.payload.isUserActionable && (
							<Text size="sm" color="red11" weight="semibold">
								Please contact Skyway support for assistance with this issue.
							</Text>
						)}
					{/* Show reasoning when expanded */}
					{isExpanded && hasReasoning && (
						<Text size="sm" color="sand11">
							<Text weight="semibold">Reasoning: </Text>
							{(item.payload as { reasoning: string }).reasoning}
						</Text>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
}

type ActivityLogProps = {
	taskId: TaskId;
};

export function ActivityLog({ taskId }: ActivityLogProps) {
	const textEditorRef = useRef<TextEditorRef>(null);
	const task = useEntity("task", taskId);
	const loggedInUser = useLoggedInUser();
	const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
	const [isRefreshedDesign] = useExperiment(Experiment.RefreshedDesign);
	const setTaskAssignee = useSetTaskAssignee(taskId);

	// Query activity log items directly instead of using task.activityLog array
	const activityLogItems = useQuery(
		"activityLogItem",
		(q) => q.where("taskId", "=", taskId).order("createdAt", "asc"),
		[taskId],
	);
	// The last rendered item, which is also what the backend gate reads
	// (`last(task.activityLogItems)`), so "the error above" is always true.
	const agentPause = agentPauseFor(task, activityLogItems.at(-1));

	const addActivityItem = useTransaction((tx, content: string) => {
		const activityId = tag<ActivityLogItemId>(crypto.randomUUID());

		// Create the activity log item only - no need to update task.activityLog
		// This eliminates the race condition where the task update could overwrite
		// the activityLog array before the server properly syncs the new item
		tx.set("activityLogItem", {
			id: activityId,
			taskId,
			createdAt: new Date(),
			content,
			createdBy: loggedInUser?.id,
			activityType: ActivityType.Comment,
			mediaItem: undefined,
			payload: {
				type: ActivityType.Comment,
				content,
			},
		});
	});

	const handleSubmit = useCallback(() => {
		const commentText = textEditorRef.current?.getValue() || "";
		if (commentText.trim()) {
			textEditorRef.current?.clear();
			textEditorRef.current?.focus();
			addActivityItem(commentText.trim());
		}
	}, [addActivityItem]);

	if (!task) return null;

	return (
		<Flex column gap="lg">
			<Flex align="center" justify="between">
				<Text size="lg" weight="semibold">
					Activity
				</Text>
			</Flex>

			{/* Activity Items */}
			<Flex column gap="lg">
				{activityLogItems.length > 0 ? (
					activityLogItems.map((item: ActivityLogItem, index: number) =>
						isRefreshedDesign ? (
							<ActivityLogItemV2
								key={item.id}
								itemId={item.id}
								isLast={index === activityLogItems.length - 1}
							/>
						) : (
							<ActivityLogItem
								key={item.id}
								itemId={item.id}
								isLast={index === activityLogItems.length - 1}
							/>
						),
					)
				) : (
					<Text size="sm" align="center" italic color="sand11">
						No activity yet
					</Text>
				)}
			</Flex>

			{/* Running Jobs Banners */}
			{task.runningJobs.length > 0 && (
				<Flex column gap="md">
					{task.runningJobs.map((job) => (
						<RunningJobBanner
							key={job.id}
							jobType={job.jobType}
							startedTime={job.startedTime}
							jobParams={job.jobParams}
							reasoning={job.reasoning}
						/>
					))}
				</Flex>
			)}

			{agentPause && <AgentPauseBanner pause={agentPause} />}

			{/* Action Controls */}
			<Flex justify="center" gap="lg" align="center">
				{task.assigneeType !== "agent" ? (
					<WithDialog
						title="Assign to agent"
						dialog={
							<Flex column gap="lg">
								<Text size="md" color="sand11">
									Assign to agent? This will let an AI autonomously complete the
									task on your behalf
								</Text>
								<Flex gap="md" justify="flex-end">
									<Button variant="outline" internal_slot="close">
										Cancel
									</Button>
									<Button
										onPress={async () => {
											// setTaskAssignee enqueues the agent itself, after the
											// ownership change commits.
											await setTaskAssignee(
												loggedInUser?.id as UserId | undefined,
												"agent",
											);
										}}
										internal_slot="close"
									>
										Assign
									</Button>
								</Flex>
							</Flex>
						}
					>
						<Button variant="outline" size="sm" color="accent9" icon="Bot">
							Assign to agent
						</Button>
					</WithDialog>
				) : (
					<Flex align="center" gap="md">
						<Text size="md" color="sand11">
							Assigned to agent.
						</Text>
						<WithDialog
							title="Unassign from agent"
							dialog={
								<Flex column gap="lg">
									<Text size="md" color="sand11">
										Unassign from agent? This will assign it back to you.
										{task.runningJobs.length > 0 && (
											<Text color="sand12" weight="medium">
												{" Currently running jobs won't be cancelled."}
											</Text>
										)}
									</Text>
									<Flex gap="md" justify="flex-end">
										<Button variant="outline" internal_slot="close">
											Cancel
										</Button>
										<Button
											onPress={async () => {
												await setTaskAssignee(
													loggedInUser?.id as UserId | undefined,
													"user",
												);
											}}
											internal_slot="close"
										>
											Unassign
										</Button>
									</Flex>
								</Flex>
							}
						>
							<Button variant="ghost" size="sm" color="accent9">
								Unassign
							</Button>
						</WithDialog>
					</Flex>
				)}
				<Button
					variant="ghost"
					size="sm"
					onPress={() => setIsProviderModalOpen(true)}
					icon="UserRoundSearch"
					style={{ color: "#9ca3af" }}
				>
					Provider directory
				</Button>
			</Flex>
			<ProviderNestedModal
				isOpen={isProviderModalOpen}
				onClose={() => setIsProviderModalOpen(false)}
			/>

			{/* Add Comment Section */}
			<Flex
				column
				gap="md"
				padding="md"
				background="background"
				borderRadius="md"
				shadow="subtle"
				style={{ cursor: "text" }}
				onClick={() => textEditorRef?.current?.focus()}
			>
				<Flex>
					<TextEditor
						ref={textEditorRef}
						size="prose-sm"
						aria-label="Activity log input"
						placeholder="Add a comment..."
						style={{ flex: 1 }}
						onSubmit={handleSubmit}
					/>
				</Flex>
				<Flex justify="end">
					<Button size="sm" variant="ghost" onPress={handleSubmit}>
						<Icon name="Reply" size="sm" />
					</Button>
				</Flex>
			</Flex>
		</Flex>
	);
}
